import React, { useEffect, useState } from "react";
import { Shield, ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTrustScore } from "@/hooks/useTrustScore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TrustScoreBadgeProps {
  userId: string;
  showDetails?: boolean;
  size?: "sm" | "md" | "lg";
}

const TrustScoreBadge: React.FC<TrustScoreBadgeProps> = ({
  userId,
  showDetails = false,
  size = "md",
}) => {
  const { getTrustScoreDetails, loading } = useTrustScore();
  const [details, setDetails] = useState<{
    score: number;
    trust_level: string;
    is_verified: boolean;
    pending_reports: number;
    active_bans: number;
    pops_received: number;
    matches_count: number;
  } | null>(null);

  useEffect(() => {
    const fetchScore = async () => {
      const data = await getTrustScoreDetails(userId);
      if (data) {
        setDetails(data);
      }
    };
    fetchScore();
  }, [userId, getTrustScoreDetails]);

  if (loading || !details) {
    return (
      <div className={cn(
        "flex items-center gap-1 text-muted-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        size === "lg" && "text-base"
      )}>
        <Loader2 className={cn(
          "animate-spin",
          size === "sm" && "w-3 h-3",
          size === "md" && "w-4 h-4",
          size === "lg" && "w-5 h-5"
        )} />
      </div>
    );
  }

  const getTrustIcon = () => {
    const iconSize = size === "sm" ? "w-3 h-3" : size === "md" ? "w-4 h-4" : "w-5 h-5";
    
    switch (details.trust_level) {
      case "excellent":
        return <ShieldCheck className={cn(iconSize, "text-emerald-500")} />;
      case "good":
        return <Shield className={cn(iconSize, "text-green-500")} />;
      case "moderate":
        return <ShieldQuestion className={cn(iconSize, "text-yellow-500")} />;
      case "low":
        return <ShieldAlert className={cn(iconSize, "text-orange-500")} />;
      case "poor":
        return <ShieldX className={cn(iconSize, "text-red-500")} />;
      default:
        return <Shield className={cn(iconSize, "text-gray-400")} />;
    }
  };

  const getTrustColor = () => {
    switch (details.trust_level) {
      case "excellent":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "good":
        return "bg-green-100 text-green-700 border-green-200";
      case "moderate":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "low":
        return "bg-orange-100 text-orange-700 border-orange-200";
      case "poor":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const badge = (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        getTrustColor(),
        size === "sm" && "px-1.5 py-0.5 text-xs",
        size === "md" && "px-2 py-1 text-xs",
        size === "lg" && "px-3 py-1.5 text-sm"
      )}
    >
      {getTrustIcon()}
      <span>{details.score}</span>
      {showDetails && (
        <span className="capitalize">{details.trust_level}</span>
      )}
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-2 p-1">
            <div className="flex items-center justify-between gap-4">
              <span className="font-semibold">Trust Score</span>
              <span className="text-lg font-bold">{details.score}/100</span>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between">
                <span>Verified:</span>
                <span>{details.is_verified ? "✓ Yes" : "No"}</span>
              </div>
              <div className="flex justify-between">
                <span>Pops Received:</span>
                <span>{details.pops_received}</span>
              </div>
              <div className="flex justify-between">
                <span>Matches:</span>
                <span>{details.matches_count}</span>
              </div>
              {details.pending_reports > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Pending Reports:</span>
                  <span>{details.pending_reports}</span>
                </div>
              )}
              {details.active_bans > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Active Bans:</span>
                  <span>{details.active_bans}</span>
                </div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TrustScoreBadge;
